/**
 * Keyboard chord normalisation.
 *
 * A chord is a lowercase, `+`-joined string in a fixed modifier order:
 *   `mod+shift+k`   — `mod` is Cmd on Apple platforms, Ctrl everywhere else.
 * Normalising to one string lets the shortcut registry be a flat Map lookup
 * instead of a per-handler predicate chain.
 */

export type Chord = string;

export const IS_APPLE =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

const MODIFIER_KEYS = new Set(['Control', 'Meta', 'Shift', 'Alt']);

/** Builds the normalised chord for a keydown event, or `null` for a bare modifier. */
export function chordFromEvent(event: KeyboardEvent): Chord | null {
  if (MODIFIER_KEYS.has(event.key)) return null;

  const parts: string[] = [];
  if (IS_APPLE ? event.metaKey : event.ctrlKey) parts.push('mod');
  if (IS_APPLE ? event.ctrlKey : event.metaKey) parts.push('ctrl');
  if (event.altKey) parts.push('alt');
  if (event.shiftKey) parts.push('shift');

  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key.toLowerCase();
  parts.push(key === ' ' ? 'space' : key);
  return parts.join('+');
}

const LABELS: Record<string, string> = {
  mod: IS_APPLE ? '⌘' : 'Ctrl',
  ctrl: IS_APPLE ? '⌃' : 'Ctrl',
  alt: IS_APPLE ? '⌥' : 'Alt',
  shift: IS_APPLE ? '⇧' : 'Shift',
  escape: 'Esc',
  arrowup: '↑',
  arrowdown: '↓',
  enter: '↵',
};

/** Human label for a chord — "⌘K" on Apple, "Ctrl + K" elsewhere. */
export function formatChord(chord: Chord): string {
  const parts = chord.split('+').map((p) => LABELS[p] ?? (p.length === 1 ? p.toUpperCase() : p));
  return IS_APPLE ? parts.join('') : parts.join(' + ');
}

/** True when focus is in a field, so global single-key shortcuts must stand down. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
